// import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  setStoredAuthToken,
  setStoredAccessToken,
  getStoredAuthToken,
} from 'utils/authToken';
import { StatusCodes } from 'http-status-codes';
import AppAWS from 'views/root/rootAWS';
import { pdfjs } from 'react-pdf';
import logger from 'utils/logger';
import localData from 'utils/localData';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 *  determine if failed http request should be retried;
 *  client side failure reasons that should have no retry:
 *    400 Bad Request: there is a problem with the request itself.
 *    403 Forbidden: Retrying without changing permissions or credentials will not succeed.
 *    404 Not Found: Retrying will not create the missing resource.
 *    405 Method Not Allowed: HTTP method not allowed; Retrying with the same method will not work.
 *    422 Unimport.metaable Entity: Server side understands the request but cannot import.meta it due to semantic errors. Retrying is futile.
 *
 */
const isRetriable = (status) => {
  const nonRetriableStatusCodes = [400, 403, 404, 405, 422];
  return !nonRetriableStatusCodes.includes(status);
};

const log = logger.createLogger('App');

// Global error handling
window.addEventListener('error', (event) => {
  log.error('Global error:', event.error, event.filename, event.lineno);
});

window.addEventListener('unhandledrejection', (event) => {
  log.error('Unhandled promise rejection:', event.reason);
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && !isRetriable(error.response.status)) {
      log.error('Non-retriable error', error);
      return Promise.reject(error); // some failure reason should have no retry;
    }

    if (
      !error.response ||
      error.response.status === 0 ||
      error.response.status === StatusCodes.UNAUTHORIZED // 401: retry with renewed auth token;
    ) {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (!session || !session.tokens) {
        console.error(error);
        log.error('Invalid session or missing tokens', session);
        // must clear sign-in session flag when token refresh fails
        localData.remove('SIGNIN_SESSION_STATUS');
        if (window.location.pathname !== '/login') {
          console.log('Redirecting to login');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      setStoredAuthToken(session.tokens?.idToken.toString());
      setStoredAccessToken(session.tokens.accessToken.toString());
      originalRequest.headers.authorization = getStoredAuthToken();
    }

    if (originalRequest.retryCount) {
      originalRequest.retryCount++;
    } else {
      originalRequest.retryCount = 1;
    }
    if (originalRequest.retryCount > 3) {
      log.error('Max retry count reached', error);
      return Promise.reject(error);
    }

    await sleep(2 ** originalRequest.retryCount * 1000);
    return axios.request(originalRequest);
  }
);

const App = AppAWS;
export default App;
